/**
 * Darstellungslogik des Historie-Reiters (`Drawer/HistoryTab/HistoryTab.tsx`, bestellbezogen):
 * dieselben vier `kategorie`-Werte mit Symbol und Farbklasse und ein Platzhalter fuer einen
 * fehlenden `summary`-Text. Extrahiert bei der Nachbesserung zu Task 13 (Pruefer-Befund:
 * wortgleiche Verdopplung) aus ZWEI Verbrauchern - der zweite war die globale Kachel
 * `OverviewPage/Historie.tsx`, die mit der Seite "Aktivitaeten" (Phase 1, Task 9) entfallen ist;
 * die Seite zeigt die serverseitig formatierten Texte (`altAnzeige`/`neuAnzeige`). Wo unten noch von
 * "beiden Verbrauchern" die Rede ist, ist dieser Stand gemeint.
 *
 * Bewusst EINE Ebene ueber beiden Verbrauchern (`components/`, nicht in einem der beiden
 * Unterordner) - keiner der beiden "gehoert" der Logik mehr als der andere.
 *
 * `betroffenerText` (`HistoryTab.tsx`) ist NICHT hier: das ist eine reine `HistoryTab`-Eigenheit
 * (aufklappbare Sammel-Details) ohne Entsprechung in der Kachel - der Pruefer hat nur
 * `KEINE_BESCHREIBUNG`/`iconFuer` als Dopplung benannt.
 */
import type { HistorienEintrag } from "../api/historie.ts";
import { fmtDateNum } from "../lib/datumsAnzeige.ts";

/** Neutraler Platzhalter fuer einen fehlenden Summary-Text (siehe `api/historie.ts`-Kopfkommentar). */
export const KEINE_BESCHREIBUNG = "(keine Beschreibung)";

/**
 * Der anzuzeigende Titeltext einer Historie-Zeile - `summary`, sofern vorhanden, sonst
 * `KEINE_BESCHREIBUNG`. Behandelt neben `null`/`undefined` (der dokumentierte Fall aus
 * `api/historie.ts`) auch einen leeren oder nur aus Leerzeichen bestehenden String gleich: ein rohes
 * `summary ?? KEINE_BESCHREIBUNG` liess einen solchen Wert unveraendert durch und renderte eine LEERE
 * Titelzeile statt des Platzhaltertexts. Quelle dieses zweiten Falls: `migration/verlauf.ts` schreibt
 * bei fehlendem Alt-Wert `summary: ""` statt `null`, weil `order_audit_log.summary` in der DB
 * `NOT NULL` ist (echtes `NULL` ist dort gar nicht moeglich) - siehe dortiger Kommentar.
 *
 * Gemeinsam genutzt von `HistoryTab.tsx` und `OverviewPage/Historie.tsx` (Auftrag "Historie-Eintraege
 * bereinigen", BE-10486) - dieselbe Begruendung wie bei `KEINE_BESCHREIBUNG`/`iconFuer` oben: keiner
 * der beiden Verbraucher soll die Pruefung ein zweites Mal, wortgleich, selbst hinschreiben.
 */
export function zeigeSummary(summary: string | null | undefined): string {
  return summary != null && summary.trim() !== "" ? summary : KEINE_BESCHREIBUNG;
}

const IST_ISO_KALENDERTAG = /^\d{4}-\d{2}-\d{2}$/;
const AUDIT_KALENDERDATUM_FELDER = new Set([
  "etd", "eta", "etd_pi", "eka_received_date", "eka_booking_date",
  "list_due_date", "air_etd", "air_eta", "air_absina", "road_etd",
  "road_eta", "paid_date", "due_date",
]);

/**
 * Wie die Oberflaeche die Phase `custom` nennt (`TasksTab/PhasenListe.tsx`: die Sammelphase der Aufgaben ohne Phase der
 * Vorlage). Das Protokoll nennt eine Phase mit ihrem Namen ("Phase 3 · Import"); nur diese Kennung kennt es nicht und
 * schreibt sie, wie sie ist (`backend/src/domaene/aufgabenPhasen.ts#phasenName`). Phase 2, Task 11B.
 */
export const PHASE_CUSTOM_LABEL = "Eigene Aufgaben";

/**
 * Die Zusammenfassung einer Phasenaenderung ("Aufgabe „<Titel>“: Phase <alt> → <neu>") mit der Bezeichnung der Oberflaeche
 * statt der Kennung `custom`. Ersetzt wird nur der Wert am Ende bzw. der hinter "Phase" - ein Titel, der "custom" heisst,
 * bleibt unberuehrt.
 */
function phaseLesbar(summary: string, alt: string | null, neu: string | null): string {
  let text = summary;
  if (neu === "custom" && text.endsWith(" → custom")) text = `${text.slice(0, -"custom".length)}${PHASE_CUSTOM_LABEL}`;
  const marke = "Phase custom → ";
  const stelle = text.lastIndexOf(marke);
  if (alt === "custom" && stelle >= 0) {
    text = `${text.slice(0, stelle)}Phase ${PHASE_CUSTOM_LABEL} → ${text.slice(stelle + marke.length)}`;
  }
  return text;
}

export function zeigeAuditSummary(
  eintrag: Pick<HistorienEintrag, "feld" | "altWert" | "neuWert" | "summary">,
): string {
  let sichtbar = zeigeSummary(eintrag.summary);
  if (eintrag.feld === "aufgabe_phase") return phaseLesbar(sichtbar, eintrag.altWert, eintrag.neuWert);
  if (!AUDIT_KALENDERDATUM_FELDER.has(eintrag.feld ?? "")) return sichtbar;
  for (const wert of [eintrag.altWert, eintrag.neuWert]) {
    if (wert != null && IST_ISO_KALENDERTAG.test(wert)) {
      sichtbar = sichtbar.split(wert).join(fmtDateNum(wert));
    }
  }
  return sichtbar;
}

/**
 * Symbol und Farbklasse je `kategorie`. `audit` (Sammel-Ereignisse an Tasks/Positionen) bekommt
 * dieselbe Farbe wie das fruehere `kind === "task"`, `ek_preis` dieselbe wie das fruehere
 * `kind === "payment"` - beides inhaltlich die naechstliegende Entsprechung zu den drei vorhandenen
 * CSS-Farbklassen (`bestand.css`, `.ops-history-icon.task/.payment/.status`). `dsv_termin` hat dort
 * keine Entsprechung und bleibt unfarbig (Standard-Optik).
 */
export function iconFuer(kategorie: HistorienEintrag["kategorie"]): { symbol: string; klasse: string } {
  switch (kategorie) {
    case "audit":
      return { symbol: "list-checks", klasse: "task" };
    case "ek_preis":
      return { symbol: "credit-card", klasse: "payment" };
    case "dsv_termin":
      return { symbol: "truck", klasse: "" };
    case "status":
    default:
      return { symbol: "refresh-cw", klasse: "status" };
  }
}
